import axios from 'axios';
import feather from 'feather-icons';
import { API_CONFIG } from '../config/api.js';

const BASE_URL = API_CONFIG.BASE_URL;
const AUTH_URL = API_CONFIG.AUTH_URL;

// Create axios instances with timeout and error handling
export const authApi = axios.create({
  baseURL: AUTH_URL,
  timeout: 60000, // 60 seconds timeout for auth operations (Render cold start can take up to 50s)
  headers: {
    'Content-Type': 'application/json',
  }
});

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add request interceptor to include token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:interceptor',message:'Request interceptor - token added',data:{url:config.url,method:config.method,hasAuthHeader:!!config.headers['Authorization']},timestamp:Date.now(),sessionId:'debug-session',runId:'session-debug',hypothesisId:'INT1'})}).catch(()=>{});
      // #endregion agent log
    } else {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:interceptor-notoken',message:'Request interceptor - NO TOKEN',data:{url:config.url,method:config.method},timestamp:Date.now(),sessionId:'debug-session',runId:'session-debug',hypothesisId:'INT2'})}).catch(()=>{});
      // #endregion agent log
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper function to sanitize sensitive data
const sanitizeData = (data) => {
  if (!data || typeof data !== 'object') return data;
  const sanitized = { ...data };
  const sensitiveKeys = ['password', 'Password', 'token', 'Token', 'secret', 'Secret', 'apiKey', 'api_key', 'apikey', 'API_KEY', 'credential', 'Credential', 'pin', 'PIN', 'otp', 'OTP'];
  
  sensitiveKeys.forEach(key => {
    if (sanitized[key]) {
      sanitized[key] = '***REDACTED***';
    }
  });
  
  // Also sanitize nested objects
  Object.keys(sanitized).forEach(key => {
    if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      sanitized[key] = sanitizeData(sanitized[key]);
    }
  });
  
  return sanitized;
};

// Function to handle login
export const loginUser = async (loginData) => {
  try {
    // Log sanitized data only (no passwords)
    const sanitizedData = sanitizeData(loginData);
    if (process.env.NODE_ENV === 'development') {
      console.log('API: Sending login request to:', AUTH_URL + '/login');
      console.log('API: Request data (sanitized):', sanitizedData);
    }
    
    const response = await authApi.post('/login', loginData);
    
    if (process.env.NODE_ENV === 'development') {
      const sanitizedResponse = sanitizeData(response.data);
      console.log('API: Response received (sanitized):', sanitizedResponse);
    }
    
    if (response.data && response.data.success) {
      const token = response.data.token;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Update the default headers for future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('API: Login successful, token stored');
      }
    }
    return response.data;
  } catch (error) {
    // Log error without sensitive data
    if (process.env.NODE_ENV === 'development') {
      console.error('API: Login error:', error.message);
    }
    
    // Handle different types of errors
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timeout. Please check your internet connection.');
    } else if (error.response) {
      // Server responded with error status - sanitize response data
      const sanitizedErrorData = sanitizeData(error.response.data);
      if (process.env.NODE_ENV === 'development') {
        console.error('API: Error response (sanitized):', sanitizedErrorData);
        console.error('API: Error status:', error.response.status);
      }
      throw new Error(error.response.data?.message || 'Login failed. Please try again.');
    } else if (error.request) {
      // Request was made but no response received
      if (process.env.NODE_ENV === 'development') {
        console.error('API: No response received');
      }
      throw new Error('Unable to connect to server. Please check your internet connection.');
    } else {
      // Something else happened
      if (process.env.NODE_ENV === 'development') {
        console.error('API: Unexpected error:', error.message);
      }
      throw new Error('An unexpected error occurred. Please try again.');
    }
  }
};

// Function to handle signup
export const signupUser = async (formData) => {
  try {
    // Log sanitized data only (no passwords)
    const sanitizedData = sanitizeData(formData);
    if (process.env.NODE_ENV === 'development') {
      console.log('API: Sending signup request with data (sanitized):', JSON.stringify(sanitizedData, null, 2));
    }
    
    const response = await authApi.post('/signup', formData);
    
    if (process.env.NODE_ENV === 'development') {
      const sanitizedResponse = sanitizeData(response.data);
      console.log('API: Signup response received (sanitized):', sanitizedResponse);
    }
    
    return response.data;
  } catch (error) {
    // Log error without sensitive data
    if (process.env.NODE_ENV === 'development') {
      const sanitizedErrorData = sanitizeData(error.response?.data);
      console.error('API: Signup error:', error.message);
      console.error('API: Error response (sanitized):', sanitizedErrorData);
      console.error('API: Error status:', error.response?.status);
    }
    
    // Re-throw with more detailed error message
    const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
    throw new Error(`Signup failed: ${errorMessage}`);
  }
};

// Function for email verification
export const verifyEmail = async (code) => {
  try {
    const response = await authApi.post('/verify-email', { code });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to resend verification code
export const resendVerificationCode = async (email) => {
  try {
    const response = await authApi.post('/resend-verification', { email });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const resetPassword = async (token, password) => {
  try {
    const response = await authApi.post(`/reset-password/${token}`, { password });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Forgot Password API method
export const forgotPassword = async (email) => {
  try {
    const response = await authApi.post('/forgot-password', { email });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to get dashboard data
export const getDashboard = async () => {
  try {
    const response = await api.get('/auth/dashboard');
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard:', error);
    throw error;
  }
};

// Function to handle logout
export const logoutUser = async () => {
  try {
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:logoutUser-start',message:'Logout started',data:{hasToken:!!localStorage.getItem('token'),hasUser:!!localStorage.getItem('user')},timestamp:Date.now(),sessionId:'debug-session',runId:'logout-debug',hypothesisId:'LO1'})}).catch(()=>{});
    // #endregion
    
    const response = await authApi.post('/logout');
    
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:logoutUser-before-clear',message:'Before clearing localStorage',data:{hasToken:!!localStorage.getItem('token'),hasUser:!!localStorage.getItem('user')},timestamp:Date.now(),sessionId:'debug-session',runId:'logout-debug',hypothesisId:'LO2'})}).catch(()=>{});
    // #endregion
    
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    
    // Clear Authorization headers
    delete api.defaults.headers.common['Authorization'];
    delete authApi.defaults.headers.common['Authorization'];
    
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:logoutUser-after-clear',message:'After clearing localStorage',data:{hasToken:!!localStorage.getItem('token'),hasUser:!!localStorage.getItem('user'),tokenValue:localStorage.getItem('token'),userValue:localStorage.getItem('user')},timestamp:Date.now(),sessionId:'debug-session',runId:'logout-debug',hypothesisId:'LO3'})}).catch(()=>{});
    // #endregion
    
    // Dispatch custom event to notify other components (like Landing page)
    window.dispatchEvent(new CustomEvent('userLogout'));
    
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:logoutUser-event-dispatched',message:'Logout event dispatched',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'logout-debug',hypothesisId:'LO4'})}).catch(()=>{});
    // #endregion
    
    return response.data;
  } catch (error) {
    console.error('Logout error:', error);
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:logoutUser-error',message:'Logout error occurred',data:{errorMessage:error?.message,errorStatus:error?.response?.status},timestamp:Date.now(),sessionId:'debug-session',runId:'logout-debug',hypothesisId:'LO5'})}).catch(()=>{});
    // #endregion
    
    // Even on error, clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    delete authApi.defaults.headers.common['Authorization'];
    window.dispatchEvent(new CustomEvent('userLogout'));
    
    throw error;
  }
};

// Function to check authentication status
export const checkAuth = async () => {
  try {
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:checkAuth-start',message:'checkAuth API called',data:{hasToken:!!localStorage.getItem('token'),tokenLength:localStorage.getItem('token')?.length,apiBaseURL:api.defaults.baseURL,hasAuthHeader:!!api.defaults.headers.common['Authorization']},timestamp:Date.now(),sessionId:'debug-session',runId:'session-debug',hypothesisId:'API1'})}).catch(()=>{});
    // #endregion agent log
    
    const token = localStorage.getItem('token');
    if (!token) {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:checkAuth-notoken',message:'No token in localStorage',data:{localStorageKeys:Object.keys(localStorage)},timestamp:Date.now(),sessionId:'debug-session',runId:'session-debug',hypothesisId:'API2'})}).catch(()=>{});
      // #endregion agent log
      throw new Error('No token found');
    }
    
    // Ensure token is in Authorization header
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:checkAuth-beforerequest',message:'About to call /auth/check-auth',data:{hasToken:true,hasAuthHeader:!!api.defaults.headers.common['Authorization']},timestamp:Date.now(),sessionId:'debug-session',runId:'session-debug',hypothesisId:'API3'})}).catch(()=>{});
    // #endregion agent log
    
    const response = await api.get('/auth/check-auth');
    
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:checkAuth-response',message:'checkAuth API response',data:{status:response.status,success:response.data?.success,hasUser:!!response.data?.user,userRole:response.data?.user?.role},timestamp:Date.now(),sessionId:'debug-session',runId:'session-debug',hypothesisId:'API4'})}).catch(()=>{});
    // #endregion agent log
    
    console.log('Auth check response:', response.data);
    return response.data;
  } catch (error) {
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:checkAuth-error',message:'checkAuth API error',data:{errorMessage:error?.message,errorStatus:error?.response?.status,errorData:error?.response?.data},timestamp:Date.now(),sessionId:'debug-session',runId:'session-debug',hypothesisId:'API5'})}).catch(()=>{});
    // #endregion agent log
    console.error('Auth check failed:', error);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    throw error;
  }
};

// Admin API functions
export const getAdminStats = async () => {
  try {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getSystemHealth = async () => {
  try {
    const response = await api.get('/admin/dashboard/system-health');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getUsers = async (page = 1, limit = 10, search = '') => {
  try {
    const response = await api.get('/admin/users', {
      params: { page, limit, search }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUserStatus = async (userId, isVerified) => {
  try {
    const response = await api.patch(`/admin/users/${userId}/status`, { isVerified });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Function to get patient dashboard data
export const getPatientDashboard = async () => {
  try {
    const response = await api.get('/patient/dashboard');
    return response.data;
  } catch (error) {
    console.error('Error fetching patient dashboard:', error);
    throw error;
  }
};

// Function to get doctor dashboard data
export const getDoctorDashboard = async () => {
  try {
    console.log('Making request to:', `${api.defaults.baseURL}/doctor/dashboard`); // Debug log
    const response = await api.get('/doctor/dashboard');
    console.log('Doctor dashboard response:', response); // Debug log
    return response.data;
  } catch (error) {
    console.error('Error fetching doctor dashboard:', error);
    throw error;
  }
};

// Initialize Feather Icons
export const initFeatherIcons = () => {
  // #region agent log
  fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:252',message:'initFeatherIcons called',data:{featherAvailable:typeof feather !== 'undefined',featherReplaceAvailable:typeof feather !== 'undefined' && !!feather.replace,iconElementsBefore:document.querySelectorAll('[data-feather]').length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
  // #endregion agent log
  
  if (typeof feather !== 'undefined' && feather.replace) {
    try {
      feather.replace();
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:257',message:'feather.replace() executed',data:{iconElementsAfter:document.querySelectorAll('[data-feather]').length,svgElementsAfter:document.querySelectorAll('svg').length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
      // #endregion agent log
    } catch (error) {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:260',message:'Error in feather.replace()',data:{error:error.message,stack:error.stack},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
      // #endregion agent log
      console.error('Error initializing Feather icons:', error);
    }
  } else {
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:265',message:'Feather not available',data:{featherType:typeof feather},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
    // #endregion agent log
  }
};

// Google OAuth API functions
export const googleSignIn = async (idToken) => {
  // #region agent log
  fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:307',message:'googleSignIn API called',data:{hasIdToken:!!idToken,idTokenLength:idToken?.length,apiUrl:authApi.defaults.baseURL,baseURL:AUTH_URL,hostname:window.location.hostname,isProduction:window.location.hostname.includes('onrender.com')},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'G'})}).catch(()=>{});
  // #endregion
  
  try {
    const response = await authApi.post('/google', { idToken });
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:315',message:'googleSignIn API response received',data:{status:response.status,success:response.data?.success,needsRoleSelection:response.data?.needsRoleSelection,needsLinking:response.data?.needsLinking},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'G'})}).catch(()=>{});
    // #endregion
    return response.data;
  } catch (error) {
    console.error('Google Sign-In error:', error);
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'api.js:321',message:'googleSignIn API error',data:{errorMessage:error.message,errorCode:error.code,errorName:error.name,errorStatus:error.response?.status,errorData:error.response?.data,hasResponse:!!error.response,hasRequest:!!error.request,apiUrl:authApi.defaults.baseURL,fullUrl:`${authApi.defaults.baseURL}/google`},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'G'})}).catch(()=>{});
    // #endregion
    
    // Handle network errors specifically
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      const apiUrl = authApi.defaults.baseURL;
      const fullUrl = `${apiUrl}/google`;
      
      console.error('Network Error Details:', {
        apiUrl,
        fullUrl,
        hostname: window.location.hostname,
        error: error.message,
        errorCode: error.code,
        timeout: authApi.defaults.timeout
      });
      
      // Check if backend is reachable
      const backendHealthUrl = apiUrl.replace('/auth', '/health');
      console.log('Checking backend health at:', backendHealthUrl);
      
      // Try to fetch backend health endpoint to diagnose the issue
      fetch(backendHealthUrl, { method: 'GET', mode: 'no-cors' })
        .then(() => console.log('Backend health check: Server is reachable'))
        .catch((healthError) => {
          console.error('Backend health check failed:', healthError);
          console.error('This suggests the backend server is down or unreachable.');
          console.error('Please check Render dashboard and restart the backend service if needed.');
        });
      
      const errorMessage = `Unable to connect to the backend server.\n\nPossible causes:\n1. Backend server is down or sleeping (Render free tier)\n2. Backend server crashed and needs restart\n3. Network connectivity issue\n\nBackend URL: ${apiUrl}\n\nPlease check the Render dashboard and restart the backend service if needed.`;
      
      return {
        success: false,
        error: errorMessage,
        message: errorMessage,
        code: 'NETWORK_ERROR',
        backendUrl: apiUrl
      };
    }
    
    // Return error in a format the frontend can handle
    if (error.response?.data) {
      return {
        success: false,
        error: error.response.data.message || error.response.data.error || 'Google Sign-In failed',
        message: error.response.data.message || error.response.data.error || 'Google Sign-In failed',
      };
    }
    throw error;
  }
};

export const completeGoogleSignup = async (data) => {
  try {
    const response = await authApi.post('/google/complete-signup', data);
    
    if (response.data && response.data.success) {
      const token = response.data.token;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Update the default headers for future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    return response.data;
  } catch (error) {
    console.error('Complete Google Signup error:', error);
    throw error;
  }
};

export const linkGoogleAccount = async (idToken, password) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('User must be authenticated to link Google account');
    }
    
    const response = await authApi.post('/google/link', { 
      idToken, 
      password 
    }, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Link Google Account error:', error);
    throw error;
  }
};


